import React, { useState, useCallback, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useBulkHistoricalData, useFilterOptions } from '@/hooks/useBulkHistoricalData';
import { applyStrategyToRaces, calculatePerformanceMetrics } from '@/utils/strategy-calculations';
import FilterPanel from './FilterPanel';
import StrategyConfigPanel from './StrategyConfigPanel';
import EnhancedResultsTable from './EnhancedResultsTable';
import PerformanceVisualization from './PerformanceVisualization';
import StrategyComparison from './StrategyComparison';
import ExportControls from './ExportControls';
import BulkAnalysisErrorBoundary from './BulkAnalysisErrorBoundary';

/**
 * Main Bulk Analysis Page Component
 * Provides comprehensive betting strategy analysis across historical race data
 */
const BulkAnalysisPage = ({ className = '' }) => {
  // State management for filters, strategies, and UI
  const [filters, setFilters] = useState({
    dateRange: { start: null, end: null },
    daysOfWeek: [],
    states: [],
    tracks: [],
    combinedOddsThreshold: null,
    horseCountRange: { min: null, max: null },
    raceTypes: []
  });

  const [strategies, setStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [selectedRaces, setSelectedRaces] = useState([]);

  // Data fetching options
  const [useTrackByTrack, setUseTrackByTrack] = useState(false);
  
  // Data fetching
  const { data: historicalDataResponse, isLoading: isLoadingData, error: dataError } = useBulkHistoricalData(filters, {
    useTrackByTrack
  });
  const { data: filterOptions = {}, isLoading: isLoadingOptions } = useFilterOptions();

  // Extract races array from the response
  const historicalData = historicalDataResponse?.races || [];
  const isDataLimited = historicalDataResponse?.dataLimited || false;

  // Apply strategy to historical data
  const processedRaces = useMemo(() => {
    // Ensure we always return an array
    if (!Array.isArray(historicalData)) {
      console.warn('BulkAnalysisPage - historicalData is not an array:', historicalData);
      return [];
    }
    
    if (!historicalData.length || !selectedStrategy) {
      return historicalData;
    }
    
    const result = applyStrategyToRaces(historicalData, selectedStrategy);
    return Array.isArray(result) ? result : [];
  }, [historicalData, selectedStrategy]);

  // Calculate performance metrics
  const performanceMetrics = useMemo(() => {
    if (!processedRaces.length || !selectedStrategy) return null;
    return calculatePerformanceMetrics(processedRaces);
  }, [processedRaces, selectedStrategy]);

  // Strategy results for visualization
  const strategyResults = useMemo(() => {
    if (!selectedStrategy || !performanceMetrics) return [];
    return [{
      strategy: selectedStrategy,
      performance: performanceMetrics,
      raceHistory: processedRaces.map(race => ({
        raceId: race.id,
        date: race.date,
        track: race.track,
        raceNumber: race.race,
        appliedBets: race.strategyApplications?.[0]?.bets || [],
        raceProfit: race.calculatedMetrics?.profit || 0,
        cumulativeProfit: 0, // Will be calculated properly in later tasks
        roi: race.calculatedMetrics?.roi || 0
      }))
    }];
  }, [selectedStrategy, performanceMetrics, processedRaces]);

  // Handlers for component interactions
  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  const handleStrategyChange = useCallback((strategy) => {
    setSelectedStrategy(strategy);
  }, []);

  const handleRaceSelect = useCallback((race) => {
    setSelectedRaces(prev => 
      prev.includes(race.id) 
        ? prev.filter(id => id !== race.id)
        : [...prev, race.id]
    );
  }, []);

  // Loading state
  if (isLoadingData || isLoadingOptions) {
    return (
      <BulkAnalysisErrorBoundary>
        <div className={`bulk-analysis-page ${className}`}>
          <div className="container mx-auto px-4 py-6">
            <div className="mb-6">
              <Skeleton className="h-8 w-64 mb-2" />
              <Skeleton className="h-4 w-96" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="lg:col-span-1 space-y-6">
                <Skeleton className="h-96 w-full" />
                <Skeleton className="h-64 w-full" />
              </div>
              <div className="lg:col-span-3 space-y-6">
                <Skeleton className="h-64 w-full" />
                <Skeleton className="h-96 w-full" />
              </div>
            </div>
          </div>
        </div>
      </BulkAnalysisErrorBoundary>
    );
  }

  // Error state
  if (dataError) {
    return (
      <BulkAnalysisErrorBoundary>
        <div className={`bulk-analysis-page ${className}`}>
          <div className="container mx-auto px-4 py-6">
            <Card className="p-8 text-center">
              <div className="text-red-600 mb-4">
                <div className="text-lg font-medium">Error loading data</div>
                <div className="text-sm">{dataError.message}</div>
              </div>
            </Card>
          </div>
        </div>
      </BulkAnalysisErrorBoundary>
    );
  }

  return (
    <BulkAnalysisErrorBoundary>
      <div className={`bulk-analysis-page ${className}`}>
        <div className="container mx-auto px-4 py-6">
          {/* Page Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Bulk Strategy Analysis
            </h1>
            <p className="text-gray-600">
              Analyse and test betting strategies against the complete historical race database
            </p>
            
            {/* Data fetching options */}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-blue-800">
                    Data Fetching Method
                  </h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Choose how to fetch historical race data for analysis
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="fetchMethod"
                      checked={!useTrackByTrack}
                      onChange={() => setUseTrackByTrack(false)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span>Standard (Faster)</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="fetchMethod"
                      checked={useTrackByTrack}
                      onChange={() => setUseTrackByTrack(true)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span>Track-by-Track (More Data)</span>
                  </label>
                </div>
              </div>
              
              {/* Show fetch stats if available */}
              {historicalDataResponse?.fetchStats && (
                <div className="mt-3 text-xs text-blue-600">
                  Fetched from {historicalDataResponse.fetchStats.tracksWithData || 'N/A'} tracks 
                  ({historicalDataResponse.fetchStats.successfulFetches} successful, {historicalDataResponse.fetchStats.failedFetches} failed)
                </div>
              )}
            </div>

            {/* Data limitation warning */}
            {isDataLimited && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Limited Data Available
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>
                        The bulk data fetch is currently limited. You may not see the complete historical dataset. 
                        Try switching to "Track-by-Track" mode for potentially more comprehensive data.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left Sidebar - Filters and Strategy Config */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="p-4">
                <h2 className="text-lg font-semibold mb-4">Filters</h2>
                <FilterPanel
                  onFiltersChange={handleFiltersChange}
                  availableOptions={filterOptions}
                  currentFilters={filters}
                />
              </Card>

              <Card className="p-4">
                <h2 className="text-lg font-semibold mb-4">Strategy Configuration</h2>
                <StrategyConfigPanel
                  onStrategyChange={handleStrategyChange}
                  savedStrategies={[]} // Will be populated with saved strategies
                  currentStrategy={selectedStrategy}
                />
              </Card>
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-3 space-y-6">
              {/* Performance Visualization */}
              {selectedStrategy && (
                <Card className="p-4">
                  <h2 className="text-lg font-semibold mb-4">Performance Analysis</h2>
                  <PerformanceVisualization
                    strategyResults={strategyResults}
                    comparisonMode={comparisonMode}
                    chartType="cumulative"
                  />
                </Card>
              )}

              {/* Strategy Comparison */}
              {comparisonMode && (
                <Card className="p-4">
                  <h2 className="text-lg font-semibold mb-4">Strategy Comparison</h2>
                  <StrategyComparison
                    strategies={[]} // Will be populated with comparison data
                    comparisonMetrics={[]}
                    onExportComparison={() => {}}
                  />
                </Card>
              )}

              {/* Export Controls */}
              <Card className="p-4">
                <ExportControls
                  onExportCSV={() => {}}
                  onExportPDF={() => {}}
                  onExportCharts={() => {}}
                  isExporting={false}
                />
              </Card>

              {/* Results Table */}
              <Card className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Race Results</h2>
                  <div className="text-sm text-gray-600">
                    {processedRaces.length} race{processedRaces.length !== 1 ? 's' : ''} loaded
                    {isDataLimited && (
                      <span className="ml-2 text-yellow-600">(Limited dataset)</span>
                    )}
                  </div>
                </div>
                <EnhancedResultsTable
                  races={processedRaces}
                  appliedStrategy={selectedStrategy}
                  onRaceSelect={handleRaceSelect}
                  selectedRaces={selectedRaces}
                  showStrategyColumns={!!selectedStrategy}
                />
              </Card>
            </div>
          </div>
        </div>
      </div>
    </BulkAnalysisErrorBoundary>
  );
};

export default BulkAnalysisPage;